import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { environment } from './../../../environments/environment';
import { UsersService } from '../services/users.service';

// Foutcodes waarmee de API zegt dat het token zelf niet (meer) deugt. Een 401 om
// een andere reden ('adminAccessRequired', 'noAccess', ...) betekent alleen dat
// je geen rechten hebt op dát object -- dan hoort je sessie te blijven staan.
const SESSION_ERRORS = [
    'accessTokenExpired',
    'accessTokenRevoked',
    'invalidAccessToken',
    'unkownAccessTokenError',
    'noAuthenticationFound',
    'Missing: x-access-token',
    'Invalid token',
    'Token expired',
];

// Hiervoor is één refresh + herhaling van de request de moeite: het refresh token
// is dan meestal nog geldig (vervallen access token, of een wachtwoordwijziging
// vanuit deze browser).
const REFRESHABLE_ERRORS = ['accessTokenExpired', 'accessTokenRevoked'];
const RETRIED_HEADER = 'x-retried-after-refresh';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {

    constructor(private usersService: UsersService, private router: Router) { }

    private endsSession(request: HttpRequest<any>, err: any): boolean {
        if (err.status !== 401) return false;
        // Een 401 van een externe API zegt niets over onze sessie.
        if (!request.url.startsWith(environment.api)) return false;
        return SESSION_ERRORS.indexOf(err.error?.error) !== -1;
    }

    // Niet meteen uitloggen: eerst het token opnieuw laten valideren. Enkel als
    // users/auth/current het ook afwijst (validateToken logt dan uit), naar de
    // loginpagina -- met returnUrl, zodat je na het inloggen terugkomt waar je was.
    private handleSessionError(): void {
        this.usersService.revalidateSession().subscribe(valid => {
            if (valid || this.usersService.token) return;
            const returnUrl = this.router.url.split('?')[0];
            if (returnUrl.startsWith('/account/')) return; // the login page itself
            this.router.navigate(['/account/login'], { queryParams: { returnUrl } });
        });
    }

    private isRefreshable(request: HttpRequest<any>, err: any): boolean {
        if (!this.endsSession(request, err)) return false;
        if (request.headers.has(RETRIED_HEADER)) return false;
        return REFRESHABLE_ERRORS.indexOf(err.error?.error) !== -1 && !!this.usersService.refreshToken;
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(catchError(err => {
            if (this.isRefreshable(request, err)) {
                return this.usersService.refreshSession().pipe(switchMap(refreshed => {
                    if (!refreshed || !this.usersService.token) {
                        this.handleSessionError();
                        return throwError(err.error?.message || err.statusText);
                    }
                    const retry = request.clone({ setHeaders: { 'x-access-token': this.usersService.token, [RETRIED_HEADER]: '1' } });
                    return next.handle(retry);
                }));
            }
            if(this.endsSession(request, err)) this.handleSessionError();

            // Some endpoints report the reason in details.error (e.g. rate limiter: { details: { error: 'Invalid ip' } })
            const error = err.error?.message || err.error?.details?.error || err.statusText;
            return throwError(error);
        }));
    }
}
